import { FC, useCallback, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import {
  DataGridPro as DataGrid,
  GridColDef,
  GridRowParams,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColumnHeaderTitle,
} from "@mui/x-data-grid-pro";
import __ from "src/utils/Translations";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import countNonNullEntries from "src/utils/data/transform/countNonNullEntries";
import mapAbsenceRequestAction from "src/utils/Company/Absences/mapAbsenceRequestAction";
import { StatusChip } from "src/Pages/Company/Absences/AbsenceRequest/StatusChip";
import CustomDetailPanelToggle from "src/Pages/Company/Absences/Tables/CustomDetailPanelToggle";
import AbsenceRequestHistoryDetails from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestHistory/AbsenceRequestHistoryDetails";
import {
  IAbsenceRequestHistory,
  IAbsenceRequestHistoryEntry,
  RequesterType,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { EPatternUnit } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";

interface IAbsenceRequestHistoryProps {
  data: IAbsenceRequestHistory;
  billingCycle: EPatternUnit;
}

const AbsenceRequestHistory: FC<IAbsenceRequestHistoryProps> = ({
  data,
  billingCycle,
}) => {
  const logStatus = useLogStatus();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  if (!data) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No Absence Request history data provided.",
      file: "AbsenceRequestHistory.tsx",
    });
  }

  if (!billingCycle) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No billingCycle provided.",
      file: "AbsenceRequestHistory.tsx",
    });
  }

  const columns = useMemo<GridColDef<IAbsenceRequestHistoryEntry>[]>(
    () => [
      {
        field: "action",
        headerName: __("Zmiana"),
        type: "string",
        width: 300,
        valueGetter: mapAbsenceRequestAction,
      },
      {
        headerName: __("Status"),
        field: "status",
        type: "string",
        width: 200,
        renderCell: ({ value }) => <StatusChip variant={value} />,
      },
      {
        field: "requester",
        headerName: __("Autor zmian"),
        type: "string",
        width: 300,
        valueGetter: ({ type, firstName, lastName }) => {
          if (type === RequesterType.SYSTEM) {
            return __("System");
          }
          if (type === RequesterType.OMB) {
            return __("Operator MyBenefit");
          }
          return `${firstName} ${lastName}`;
        },
      },
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        width: 200,
        renderHeader: () => (
          <GridColumnHeaderTitle label={__("Szczegóły")} columnWidth={200} />
        ),
        renderCell: (params) => {
          const detailRowCount = params.row.details
            ? countNonNullEntries(
                params.row.details as unknown as Record<string, unknown>
              )
            : 0;

          return detailRowCount ? <CustomDetailPanelToggle /> : "-";
        },
      },
      {
        field: "actionTime",
        headerName: __("Data zmiany"),
        type: "dateTime",
        width: 175,
        sortable: true,
        valueGetter: (value) => new Date(value),
      },
    ],
    []
  );

  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams) => (
      <AbsenceRequestHistoryDetails
        data={row.details}
        billingCycle={billingCycle}
      />
    ),
    [billingCycle]
  );

  const getDetailPanelHeight = useCallback(({ row }: GridRowParams) => {
    const detailRowCount = countNonNullEntries(row.details);

    return 120 + detailRowCount * 60;
  }, []);

  return (
    <>
      <Typography variant="h5">{__("Historia zmian na wniosku")}</Typography>
      <DataGrid
        rows={data.entries}
        columns={columns}
        getRowId={(row) => `${row.order}-${row.actionTime}`}
        initialState={{
          sorting: {
            sortModel: [{ field: "actionTime", sort: "desc" }],
          },
        }}
        disableColumnMenu
        disableEval
        hideFooterRowCount
        density="comfortable"
        rowSelection={false}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
      />
    </>
  );
};

export default AbsenceRequestHistory;
